
import { Component, Vue, Watch } from "vue-property-decorator";
import { namespace, Getter } from "vuex-class";
import vSelect3 from "vselect3";
import Multiselect from "vue-multiselect";
import { Datetime } from "vue-datetime";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import { UserPermissions } from '@/store/types';
import { ReportState, ShareCustomReportModal, InsightSharesData, ShareWithUsers,
        ReportSharedWith } from "@/store/reports/types";
import ConfirmationModal from "@/components-shared/ConfirmationModal.vue"
import { AxiosResponse } from 'axios';
import CustomReportsApi, { ShareCustomReportResponse, ShareDataCustomReportResponse } from "@/services/api.customReports.service";
import { ResourceType } from '@/store/sessions/types';

const ReportsStore = namespace("reports");
const SiteMonitor = namespace("siteMonitor");

@Component({
    components: {
			"v-select3": vSelect3,
            multiselect: Multiselect,
            datetime: Datetime,
            "confirmation-modal": ConfirmationModal,
            VuePerfectScrollbar,
    }
})
export default class ShareCustomReports extends Vue {
    @Getter getPermissions: UserPermissions;
    @Getter getUserId: number;

    @ReportsStore.Mutation setShareCustomReportModalShown: (shareCustomReportModal: ShareCustomReportModal) => void;
    @ReportsStore.State((state: ReportState) => state.ShareCustomReportModal) private shareCustomReportModal: ShareCustomReportModal;
    @ReportsStore.Action getUsersAndGroups: (query: string) => void;
    @ReportsStore.Action fetchReportTypes: () => Promise<void>;
    @ReportsStore.State((state: ReportState) => state.ShareWithUsers) private availableUsers: ShareWithUsers[];

    readonly shareCustomReportStatusSuccessful: number = 200;

    private noExpiryDate: boolean = false;
    private tempShareWith: ReportSharedWith[];
    private showRemoveShare: boolean = false;
    private formData: InsightSharesData = {
        insightID: 0,
        publicShare: false,
        expiryDate: null,
        sharedWith: []
    };

    private showConfirmationModal: boolean = false;
    private confirmationModalMessage: string = null;
    private confirmModalOkButtonText: string = null;
    private confirmModalCancelButtonText: string = null;
    private confirmModalDeletion: boolean = false;
    private confirmModalTitle: string = "Confirmation Modal";

    public closeShareCustomReportModal() {
        this.formData = {
            insightID: 0,
            publicShare: false,
            expiryDate: null,
            sharedWith: []
        };
        this.usersToShareWith = [];
        this.showRemoveShare = false;
		this.setShareCustomReportModalShown({shown: false, insightID:0, reportName: ""});
	}

    private get enableShareBtn(): boolean{
        const data: InsightSharesData = this.formData;

        if (!data.publicShare && data.sharedWith !== null && data.sharedWith.length === 0) {
            return false;
        }
        
        if (!this.noExpiryDate && new Date(data.expiryDate) < new Date()) {
            return false;
        }

        return true;
    }

    private get dateWarning(): string{
        const data: InsightSharesData = this.formData;
        const noExpiryDate = this.noExpiryDate;
        
        if(!noExpiryDate){
            if(data.expiryDate == null){
                return "You must enter a date before submitting."
            } 
            else if(new Date(data.expiryDate) < new Date()) {
                return "Date entered is in the past. Please enter a future date."
            }
        }

        return null;
    } 

    @Watch('shareCustomReportModal.shown')
    onModalShown(): void{
       this.getShareData();
    }

    private async getShareData(){
        let response: AxiosResponse<ShareDataCustomReportResponse> = await CustomReportsApi.getSharesForCustomReport(this.shareCustomReportModal.insightID);
        if (response && response.data && response.status === this.shareCustomReportStatusSuccessful) {
            if (response.data.successful == true){
                this.formData.insightID = response.data.shareCustomReportRequest.insightID;
                this.formData.publicShare = response.data.shareCustomReportRequest.publicShare;
                this.formData.expiryDate = response.data.shareCustomReportRequest.expiryDate;
                this.formData.sharedWith = response.data.shareCustomReportRequest.sharedWith;
                
                // If no expiry date check box
                if(this.formData.expiryDate == null){
                    this.noExpiryDate = true;
                } 
                else {
                    this.noExpiryDate = false
                }
                
                // If shared show remove button
                if(this.formData.publicShare || this.formData.sharedWith.length > 0){
                    this.showRemoveShare = true;
                }
                else {
                    this.showRemoveShare = false;
                }
            }
        }        
    }

    private noExpiryDateClicked(): void{
        if(this.noExpiryDate == true){
            this.formData.expiryDate = null;
        }
    }
    
    private usersToShareWith: ShareWithUsers[] = [];
    private isLoading: boolean = false;
    private async viewShareFind(query: string) {
        this.isLoading = true;
        await this.getUsersAndGroups(query);
        this.isLoading = false;
    }

    private removeSelectedUser(user : ShareWithUsers) {
		this.usersToShareWith.splice(this.usersToShareWith.indexOf(user), 1);
	}

    private publicShare(){
        if(this.formData.publicShare){
            //Clear add box once added
            this.usersToShareWith = [];

            //Store sharedWith if public share is removed
            if(this.formData.sharedWith.length > 0){
                this.tempShareWith = this.formData.sharedWith;
            }   
            this.formData.sharedWith = [];
        } 
        else {
            //if tempSharedWith is used restore data
            if(this.tempShareWith != null){ 
                if(this.tempShareWith.length > 0){
                    this.formData.sharedWith = this.tempShareWith;
                }
            }  
            this.tempShareWith = [];
        }
    }

    private addToShare(){
        if(this.usersToShareWith.length > 0 && !this.formData.publicShare){

            this.usersToShareWith.forEach(share => {
                let userID: number = share.userID;
                let isGroup: boolean = false;
               
                if(userID == null){
                    userID = share.userGroupID;
                    isGroup = true
                }

                this.formData.sharedWith.push({
                    id: userID,
                    title: share.name,
                    isGroup: isGroup,
                    initials: share.initials
                })
            })
            
            //Clear add box once added
            this.usersToShareWith = [];
        }
    }

    private removeShare(viewShare: ReportSharedWith){
        this.formData.sharedWith.splice(this.formData.sharedWith.indexOf(viewShare), 1);
    }

    private openConfirmationModal(title: string, message: string, deletion: boolean, okText: string, cancelText: string) {
        this.confirmModalDeletion = deletion;
        this.confirmModalOkButtonText = okText;
        this.confirmModalCancelButtonText = cancelText;
        this.confirmationModalMessage = message;
        this.confirmModalTitle = title;
        this.showConfirmationModal = true;
    }

    private closeConfirmationModal() {     
        this.showConfirmationModal = false;
        this.confirmModalDeletion = false;
        this.confirmModalOkButtonText = "";
        this.confirmModalCancelButtonText = "OK";
        this.confirmationModalMessage = "";
        this.confirmModalTitle = "";
    }
    
    private async shareCustomReport(){

        let response: AxiosResponse<ShareCustomReportResponse> = await CustomReportsApi.shareCustomReport(this.formData);
        if (response && response.data && response.status === this.shareCustomReportStatusSuccessful) {
            if (response.data.successful == true){
                await this.fetchReportTypes();
                this.closeShareCustomReportModal();
            }
            else {
                this.openConfirmationModal("Share Report Error", response.data.error, false,"","OK");
            }
        }
        else {
            this.openConfirmationModal("Share Report Error", "Error: No resonse from server.", false, "", "OK")
        }
    }

    private async confirmRemoveAllShares(){
        this.openConfirmationModal("Stop Sharing - " + this.shareCustomReportModal.reportName,
                                    "Are you sure you want to stop sharing the report?",
                                    true,
                                    "Remove Share",
                                    "Cancel");
    }

    private async removeAllSharesCustomReport(){
        let response: AxiosResponse<ShareCustomReportResponse> = await CustomReportsApi.removeAllSharesCustomReport(this.shareCustomReportModal.insightID);
        if (response && response.data && response.status === this.shareCustomReportStatusSuccessful) {
            if (response.data.successful == true){
                this.closeConfirmationModal();
                await this.fetchReportTypes();
                this.closeShareCustomReportModal();
            } 
            else {
                this.openConfirmationModal("Remove Share Error", response.data.error, false, "", "OK")
            }
        } 
        else {
            this.openConfirmationModal("Remove Share Error", "Error: No resonse from server.", false, "", "OK");
        }
    }
}
